/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC} from 'react'
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom'
import {App} from '../App'
import {AuthPage, Logout, useAuth} from '../modules/auth'
import {AuthLayout} from '../modules/auth/AuthLayout'
import {CustomError} from '../modules/errors/components/CustomError'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {DigitalCard} from '../modules/registration/DigitalCard'
import {Registration} from '../modules/registration/Registration'
import {PrivateRoutes} from './PrivateRoutes'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
  const {currentUser} = useAuth()
  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          {currentUser ? (
            <>
              <Route path='/*' element={<PrivateRoutes />} />
              <Route index element={<Navigate to='/companies' />} />
            </>
          ) : (
            <Route element={<AuthLayout />}>
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='registration/:token' element={<Registration />} />
              <Route path='digitalcard' element={<DigitalCard />} />
              <Route path='error' element={<CustomError />} />
              {/* Admin areas send a signed-out visitor to the sign-in form. They used to fall
                  through to the catch-all below, so an admin whose session had ended - or who
                  followed a bookmark - was dropped on the public donor signup page with no way
                  back in. The bare domain deliberately still goes to the donor page. */}
              <Route path='companies/*' element={<Navigate to='/auth/login' replace />} />
              <Route path='settings/*' element={<Navigate to='/auth/login' replace />} />
              <Route path='crafted/*' element={<Navigate to='/auth/login' replace />} />
              <Route path='*' element={<Navigate to='digitalcard' />} />
            </Route>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
